import {
  ConditionOperator,
  ConditionValueType,
  ConditionalStepGroupInputZod,
  PlaybookElseBranchStepInputZod,
  PlaybookInputZod,
  getHumanReadableConditionOperator,
  workflowProcessMetadataOptions,
} from "@avenueops/shared-types";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { InputOption, MarkdownViewer, SmartForm } from "@avenueops/web-components";
import { PgTypeIcon } from "components/visual-query-editor/icons/PgTypeIcon";
import FilterValueField from "components/visual-query-editor/filters/FilterValueField";
import { WhereFilterValueType } from "@avenueops/visual-query-editor";

export const IfBranchBlock = ({ step, index }: { step: ConditionalStepGroupInputZod; index?: number }) => {
  const branch = step;
  return (
    <>
      {branch.condition ? (
        <div className="text-sm my-1">
          {index && index > 0 ? "Else if" : "If"}{" "}
          <span className="font-semibold border-b border-dashed border-gray-300 mr-1 pb-[1.5px]">{branch.condition.columnName}</span>
          {getHumanReadableConditionOperator(branch.condition.operator)}{" "}
          <span className="font-semibold border-b border-dashed border-gray-300 mr-1 pb-[1.5px]">
            {(branch.condition as any)?.value ? (branch.condition as any).value.value : "..."}
          </span>
        </div>
      ) : (
        <div className="text-sm my-1">Else</div>
      )}
    </>
  );
};

export const ElseBranchBlock = ({ step }: { step: PlaybookElseBranchStepInputZod }) => {
  return (
    <span className="text-sm">
      <MarkdownViewer value={step.label} />
    </span>
  );
};

export function IfBranchStepInput({ stepPrefix, columns, index }: { stepPrefix: `steps.${number}.branches.${number}`; columns: string[]; index?: number }) {
  const { setValue } = useFormContext<PlaybookInputZod>();
  const branch = useWatch({ name: stepPrefix }) as ConditionalStepGroupInputZod;
  const columnOptions = columns.map((column) => ({ value: column, label: column }));

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex gap-1">
        <div className="flex items-center">{index && index > 0 ? "Else if" : "If"}</div>
        <div className="flex-1">
          <SmartForm.DropdownSelect
            name={`${stepPrefix}.condition.columnName` as const}
            options={columnOptions.concat(workflowProcessMetadataOptions)}
            placeholder="Select column"
            onCreate={(value) => {
              setValue(`${stepPrefix}.condition.columnName`, value);
              return {
                value,
                label: value,
              };
            }}
            onChangeCallback={() => {
              setValue(`${stepPrefix}.condition.value.value` as const, null as any, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
            usePortal
          />
        </div>
        <div className="px-2">
          <SmartForm.DropdownSelect
            name={`${stepPrefix}.condition.operator` as const}
            type="OUTLINE"
            defaultValue={ConditionOperator.EQUALS}
            options={
              Object.values(ConditionOperator).map((operator) => ({
                value: operator,
                label: getHumanReadableConditionOperator(operator),
              })) as any
            }
            onChangeCallback={(operator) => {
              const allowedTypes = allowedTypesByOperator(operator as unknown as ConditionOperator);
              setValue(`${stepPrefix}.condition.value.type`, allowedTypes?.[0] ?? null, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
            placeholder="Select operator"
            usePortal
          />
        </div>
        <div className="flex-1">
          <IfElseConditionValueInput stepPrefix={stepPrefix} columnOptions={columnOptions} />
        </div>
      </div>
    </div>
  );
}

export function ElseBranchStepInput({ stepPrefix, columns }: { stepPrefix: `steps.${number}.elseBranch`; columns: string[] }) {
  const { setValue } = useFormContext<PlaybookInputZod>();

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center gap-2">
        <SmartForm.TextInput title="Else branch label" name={`${stepPrefix}.label`} />
      </div>
    </div>
  );
}

function allowedTypesByOperator(operator?: ConditionOperator): ConditionValueType[] {
  if (!operator) {
    return [];
  }
  switch (operator) {
    case ConditionOperator.EQUALS:
    case ConditionOperator.NOT_EQUALS:
      return [ConditionValueType.STRING, ConditionValueType.NUMBER, ConditionValueType.BOOLEAN, ConditionValueType.DATE, ConditionValueType.COLUMN];
    case ConditionOperator.GREATER_THAN:
    case ConditionOperator.GREATER_THAN_OR_EQUAL:
    case ConditionOperator.LESS_THAN:
    case ConditionOperator.LESS_THAN_OR_EQUAL:
      return [ConditionValueType.NUMBER, ConditionValueType.DATE, ConditionValueType.COLUMN];
    case ConditionOperator.CONTAINS:
    case ConditionOperator.NOT_CONTAINS:
      return [ConditionValueType.STRING, ConditionValueType.COLUMN];
    case ConditionOperator.IS_NULL:
    case ConditionOperator.IS_NOT_NULL:
    default:
      return [];
  }
}

const IfElseConditionValueInput = ({ stepPrefix, columnOptions }: { stepPrefix: string; columnOptions: InputOption[] }) => {
  const { setValue, watch } = useFormContext();
  const operator = watch(`${stepPrefix}.condition.operator` as any) as unknown as ConditionOperator;
  const valueType = watch(`${stepPrefix}.condition.value.type` as any) as unknown as ConditionValueType;
  const allowedTypes = allowedTypesByOperator(operator);

  if (!operator || allowedTypes.length === 0) {
    return null;
  }

  const typeOptions = allowedTypes.map((type) => ({ value: type, label: type, icon: <PgTypeIcon pgType={type} className="w-4 h-4" /> }));

  return (
    <div className="col-span-5 flex items-end">
      <div className="bg-gray-100 flex items-center justify-center my-1 w-16 h-10 border rounded-l border-cool-gray-300">
        <div>
          <SmartForm.DropdownSelect
            name={`${stepPrefix}.condition.value.type`}
            type="INLINE"
            hideSelectedLabel
            hideSearchBar
            defaultValue={valueType ?? typeOptions?.[0]?.value ?? null}
            onChangeCallback={() => {
              setValue(`${stepPrefix}.condition.value.value`, null, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
            options={typeOptions}
            usePortal
          />
        </div>
      </div>
      <div className={`[&_.border]:border-l-0 [&_.border]:rounded-l-none w-full`}>
        {valueType && typeOptions.length > 0 ? (
          <FilterValueField type={valueType as unknown as WhereFilterValueType} prefix={`${stepPrefix}.condition.value`} columOptions={columnOptions} />
        ) : null}
      </div>
    </div>
  );
};
